/**
 * @fileoverview In-chat partner-sharing ceremony — a CHAT-OWNED host for
 * `SharingDetailPanel`'s consent queue.
 *
 * The completion ceremony's second beat offers a "Partner Sharing" artifact card.
 * Clicking it opens the SAME queue the /partner registry opens ("Review N
 * topics"), in the SAME right panel, without leaving the completion the user
 * just earned. `SharingDetailPanel` owns every phase — the pre-filled
 * overview, the optional one-by-one walk, the staging layer and the
 * overview's one submit; this module supplies only a `host` and a lifecycle.
 *
 * Two hosts, one contract:
 *   - `SharingRegistry._panelHost()` — the /partner registry (long-lived).
 *   - this module — the chat ceremony (one run, then gone).
 * Both derive their ledger from `sharingLedgerModel.ledgerFromStatus` and both
 * guard their submit with `ledgerFromSubmitResponse`, so the view→ledger
 * mapping and the malformed-200 defence cannot drift between them.
 *
 * Lifecycle is modeled on `components/QuestionnaireLauncher.js` (bound
 * `right-panel:closed` listener + explicit `destroy()`), with
 * `orchestration/composeNoteLauncher.js`'s module-scoped in-flight guard and
 * post-await supersession check. Chat is not a PageManager section, so nothing
 * tears a chat panel down on its own: `ModeOrchestrator.returnToDashboard()`
 * AND `ModeOrchestrator.switchMode()` both call {@link destroySharingCeremony}.
 * A ceremony panel that outlives its chat context is a bug.
 *
 * @module chat/sharingCeremonyLauncher
 */

import { DiscoveryAPI } from '../core/discoveryApi.js';
import { eventBus } from '../core/utils.js';
import { SharingDetailPanel } from '../sections/partnerSharing/SharingDetailPanel.js';
import {
  ledgerFromStatus,
  ledgerFromSubmitResponse,
  gestaltGroupId,
  needsReviewCount,
  queueScope
} from '../sections/partnerSharing/sharingLedgerModel.js';

/**
 * Sharing-page states that carry no reviewable disclosure set. `openQueue`'s
 * own empty-scope guard is a SILENT no-op, so these are caught here and given
 * an honest reason panel instead.
 * @type {Set<string>}
 */
export const NOT_ACTIONABLE_STATES = new Set(['generating', 'locked', 'error']);

/**
 * The honest failure message for the two host seams that only single mode can
 * reach. See {@link SharingCeremonyLauncher#_panelHost}.
 */
const SINGLE_MODE_MESSAGE = 'Open your Partner page to change this.';

/** Where every reason panel sends the user for the long-lived surface. */
const PARTNER_ROUTE = '/partner?tab=sharing';

/** `contentType` the reason panels claim on the shared right panel. */
export const NOTICE_CONTENT_TYPE = 'sharing-ceremony-notice';

/**
 * The one live ceremony, if any — the owner of WHATEVER ceremony content is on
 * the shared panel, queue or reason notice alike. Module-scoped because the
 * teardown hooks (`returnToDashboard`, `switchMode`, `clearModeDisplay`) have
 * no instance to reach through.
 * @type {SharingCeremonyLauncher|null}
 */
let active = null;

/**
 * Re-entrancy guard: the invitation button isn't disabled while the status
 * read is in flight, and a double-click must not open two ceremonies.
 * @type {boolean}
 */
let launchInFlight = false;

/**
 * Monotonic launch identity. A teardown bumps it, which is what lets an
 * ALREADY-STARTED `getSharingStatus` read know it no longer has a chat to open
 * into: page and session are both still what they were when a `switchMode`
 * teardown lands before the new session's id exists, so those two checks alone
 * would let an orphaned panel open AFTER the teardown ran.
 * @type {number}
 */
let launchToken = 0;

/**
 * Build one reason panel: title, body, and the standing Partner-page link.
 * Text goes in via `textContent` (the `practiceReferencePanel` idiom) — no
 * markdown, no interpolation into markup.
 *
 * @param {{title: string, body: string}} copy
 * @returns {HTMLElement}
 */
function buildNoticePanel({ title, body }) {
  const root = document.createElement('div');
  root.className = 'sharing-notice';

  const heading = document.createElement('h1');
  heading.className = 'sharing-notice__title';
  heading.textContent = title;
  root.appendChild(heading);

  const text = document.createElement('p');
  text.className = 'sharing-notice__body';
  text.textContent = body;
  root.appendChild(text);

  const link = document.createElement('a');
  link.className = 'sharing-notice__link';
  link.href = PARTNER_ROUTE;
  link.textContent = 'Open Partner page';
  root.appendChild(link);

  return root;
}

/**
 * The register generation races the completion moment
 * (`maybeEnqueuePartnerSnapshots` fires before the completion response is
 * built), so this is the COMMON case at Foundation completion.
 */
const PREPARING_NOTICE = {
  title: 'Still preparing',
  body: "We're still preparing what you could share — it'll be ready in a few minutes. You can always review from your Partner page."
};

/** The read itself failed. Never silent, never a spinner that never resolves. */
const ERROR_NOTICE = {
  title: "Couldn't load your sharing choices",
  body: "Something went wrong reading what you could share. You can review it any time from your Partner page."
};

/**
 * The set was already confirmed by the time the invitation was clicked (a
 * second tab, or a mid-ceremony decision on /partner). Mirrors the predicate
 * `SharingRegistry._reviewBannerHtml` suppresses its whole banner on, so the
 * two surfaces agree on what "settled" means.
 * @param {string} partnerName
 * @returns {{title: string, body: string}}
 */
function settledNotice(partnerName) {
  return {
    title: "You're all set",
    body: `You've already approved what's shared with ${partnerName}. You can change any of it from your Partner page.`
  };
}

/**
 * Build one of the three reason panels by name, without a launch, a status
 * read, or an owner. The ONLY reason this is exported: the dev-tools
 * design-preview namespace (`dev.sharedGuides.notice`) renders these panels
 * render-only, and copy that exists twice is copy that drifts.
 *
 * @param {'preparing'|'settled'|'error'} state
 * @param {Object} [opts]
 * @param {string} [opts.partnerName] - Only read by the `settled` copy.
 * @returns {HTMLElement|null} Null for an unrecognized state.
 */
export function buildSharingNoticeElement(state, { partnerName = 'your partner' } = {}) {
  const copy = state === 'preparing' ? PREPARING_NOTICE
    : state === 'error' ? ERROR_NOTICE
      : state === 'settled' ? settledNotice(partnerName)
        : null;
  return copy ? buildNoticePanel(copy) : null;
}

/**
 * Whether this launch has been superseded since its `await`: a teardown ran
 * (token bumped), chat is no longer the surface on screen, or the session moved
 * on. A benign supersede — never an error.
 *
 * The TOKEN is the load-bearing one. `switchMode()` tears the ceremony down
 * before it starts the new backend session, so at that instant the page is
 * still `chat` and the session id is still the OLD one — page and session both
 * look unchanged, and without the token an in-flight read would resolve
 * straight into an orphaned panel over the incoming session's chat.
 *
 * @param {number} token - Value of `launchToken` when this launch began
 * @param {string|null} sessionAtLaunch
 * @returns {boolean}
 */
function isSuperseded(token, sessionAtLaunch) {
  if (launchToken !== token) return true;
  const pageManager = window.app?.modules?.pageManager;
  if (typeof pageManager?.getCurrentPage === 'function' && pageManager.getCurrentPage() !== 'chat') {
    return true;
  }
  const now = window.sessionManager?.getCurrentSessionId?.() ?? null;
  return now !== sessionAtLaunch;
}

/**
 * One ceremony run — the OWNER of whatever ceremony content is on the shared
 * right panel, and of the `right-panel:closed` subscription that retires it.
 *
 * Two shapes: a queue run (a `SharingDetailPanel` over a real ledger) and a
 * reason notice (no panel, no ledger — just the static content this module
 * built). Both are owned, because a teardown has to be able to close EITHER:
 * a "still preparing" notice orphaned over a new session is the same bug as an
 * orphaned queue.
 */
class SharingCeremonyLauncher {
  /**
   * @param {Object} [config]
   * @param {Object|null} [config.ledger] - `{masters, groups, units}` from
   *   `ledgerFromStatus`; null for a notice-only owner (no panel is built).
   * @param {string} [config.partnerName]
   * @param {{submitSharing: Function}|null} [config.api] - Consent write seam
   * @param {Function|null} [config.onResolved] - Called ONCE, as
   *   `onResolved({succeeded, bothConfirmed})`, when this ceremony resolves on its own — the
   *   approve-success auto-close (`succeeded: true`) or the user closing the
   *   panel without submitting anything, by × or the overview's "Done"
   *   (`succeeded: false`). NOT called on a
   *   navigation teardown. The chat host routes it into
   *   `ModeOrchestrator.resolveSharingDecision`, which owns the whole branch:
   *   the card completes and phase 3 opens on `succeeded` (closing without
   *   submitting must never claim a share that didn't happen), or the card
   *   reverts to `unfinished` and the decision is re-offered otherwise.
   *   `bothConfirmed` (success only) selects the acknowledgement variant.
   */
  constructor({ ledger = null, partnerName = 'your partner', api = null, onResolved = null } = {}) {
    this.ledger = ledger;
    this.partnerName = partnerName;
    this.api = api;
    this._destroyed = false;
    /**
     * One-shot resolution callback; nulled on use and on a forced teardown.
     * @type {Function|null}
     */
    this._onResolved = typeof onResolved === 'function' ? onResolved : null;
    /**
     * The `contentType` this launcher currently has on the panel, or null when
     * it owns nothing. It is BOTH the close match and the ownership flag: a
     * teardown emits a close only while this is set, so it can never shut a
     * panel some other surface has since taken over.
     * @type {string|null}
     */
    this._contentType = null;

    /**
     * Whether the partner had already confirmed their own side at the moment
     * this run's submit landed — read off the submit response's own view
     * (`gestaltConfirmedByPartner`), which is the wire form of the backend's
     * `bothConfirmed`: `setDiscoveryMaster` clears `gestaltConfirmedAt` in
     * BOTH directions, so a live partner confirm implies a live partner
     * master. Threaded out through `_notifyResolved` because the chat host's
     * acknowledgement copy would otherwise promise a couple-level synthesis
     * that has not been built: it only builds once BOTH members confirm, and
     * whoever shares first is the common case, not an edge one.
     * @type {boolean}
     */
    this._bothConfirmed = false;

    /**
     * Whether this launcher disabled the chat input for its queue run (only
     * `open()` sets it — reason notices never lock). Read by `_retire`, whose
     * `input:enabled` is the PRIMARY unlock on two of the three chat exits.
     * @type {boolean}
     */
    this._inputLocked = false;

    this._boundPanelClosed = this._onPanelClosed.bind(this);
    eventBus.on('right-panel:closed', this._boundPanelClosed);

    /** @type {SharingDetailPanel|null} Null for a notice-only owner. */
    this.panel = ledger ? new SharingDetailPanel({ eventBus, host: this._panelHost() }) : null;
  }

  /**
   * The eight-key `host` contract `SharingDetailPanel` reads and writes through.
   *
   * `setUnitState` / `approveUnit` are structurally unreachable here: the
   * panel calls them only from `_approveSingle()`, which is reachable only in
   * single mode (`openSingle`), and this host only ever calls
   * `panel.openQueue(groupId)` — a queue run never renders a per-topic
   * Approve on a walk step or an overview drill; its overview's one
   * "Approve & share" goes through `submitGroup`. They are supplied for
   * contract completeness and REJECT with an honest message rather than
   * writing a local-only value that never persists: `_commitUnit`'s failure
   * path rolls the row back and surfaces `err.error` verbatim, so any future
   * reachable path fails loudly instead of silently lying.
   *
   * `requestClose` carries the panel's two close meanings: an ARRAY of
   * changed unit ids is the approve-success auto-close (a real share); no
   * argument is the overview's "Done" dismiss (nothing written). Only the
   * first may resolve the ceremony as succeeded.
   *
   * @private
   * @returns {Object}
   */
  _panelHost() {
    return {
      getLedger: () => this.ledger,
      getPartnerName: () => this.partnerName,
      setUnitState: () => Promise.reject({ error: SINGLE_MODE_MESSAGE }),
      approveUnit: () => Promise.reject({ error: SINGLE_MODE_MESSAGE }),
      submitGroup: (groupId, unitStates) => this._submitGroup(unitStates),
      // Nothing behind this panel to repaint — the chat host renders no
      // registry list, and /partner holds its own instance and its own poll.
      onLedgerChanged: () => {},
      onPanelOpened: (contentType) => { this._contentType = contentType; },
      requestClose: (changed) => this.close(Array.isArray(changed))
    };
  }

  /**
   * The run's ONE write. `groupId` is deliberately dropped on the final hop —
   * `POST /sharing/submit` takes `unitStates` only (D2b owns the wire-level
   * group), exactly as `SharingRegistry.submitGroup` does.
   *
   * The response is re-mapped into the ledger so a late paint reads server
   * truth; a replacement (rather than the registry's in-place merge) is safe
   * here because no row was captured for rollback — single mode, the only
   * capture site, is unreachable — and the panel closes on the next tick.
   *
   * @private
   * @param {Record<string, 'full'|'basic'|'off'>} unitStates
   * @returns {Promise<void>}
   * @throws {{error: string}} When the response carries no unit ledger
   */
  async _submitGroup(unitStates) {
    const res = await this.api.submitSharing(unitStates);
    const view = ledgerFromSubmitResponse(res);
    // Captured before the ledger map — `ledgerFromStatus` keeps only the
    // consent rows, and this is the one moment the partner's own confirm state
    // is on the wire.
    this._bothConfirmed = Boolean(view?.gestaltConfirmedByPartner);
    const next = ledgerFromStatus(view);
    if (next) this.ledger = next;
  }

  /**
   * Open the queue over one group's disclosure set. The panel calls
   * `onPanelOpened` on every phase emit, which is what records ownership.
   *
   * Also locks the chat input for the run — the questionnaire's own overlay
   * mechanism (`QuestionnaireLauncher._setInputDisabled`): the disabled
   * overlay covers the composer AND the in-input "Maybe later" button row via
   * z-index, so the whole input region reads as "the panel owns this
   * conversation right now". Released by `_retire`, on every path. Reason
   * notices (`openNotice`) deliberately never lock — they aren't the workflow,
   * just an explanation of why it couldn't open.
   *
   * @param {string} groupId
   */
  open(groupId) {
    this._inputLocked = true;
    eventBus.emit('input:disabled', {
      title: 'Partner Sharing in Progress',
      subtitle: 'Close the panel to resume your conversation'
    });
    this.panel.openQueue(groupId);
  }

  /**
   * Show a reason panel in place of the queue, and OWN it. Ownership is
   * recorded here rather than through `onPanelOpened` because a notice has no
   * `SharingDetailPanel` behind it — without this the notice would be
   * untouchable by teardown, which is exactly how one used to orphan itself
   * over a new session.
   *
   * @param {{title: string, body: string}} copy
   */
  openNotice(copy) {
    this._contentType = NOTICE_CONTENT_TYPE;
    eventBus.emit('right-panel:open', {
      title: '',
      content: buildNoticePanel(copy),
      contentType: NOTICE_CONTENT_TYPE
    });
  }

  /**
   * Panel × / cross-detail close. Exact match against the CURRENTLY open
   * content type, not set membership — the queue re-emits on every phase
   * change (overview ⇄ walk step), so a stale `sharing-queue` close landing
   * after the overview re-opened must not retire the phase that superseded
   * it. Another section's panel closing never matches either.
   *
   * The panel is already gone here, so ownership is dropped BEFORE the
   * teardown: re-emitting a close for a panel the manager just closed would
   * shut whatever opened next.
   *
   * @private
   * @param {{contentType?: string}} [data]
   */
  _onPanelClosed(data = {}) {
    if (this._destroyed || !this._contentType) return;
    if (data?.contentType !== this._contentType) return;
    this._contentType = null;
    this.panel?.handleClosed();
    // The user closed OUR panel (× or a cross-detail open) WITHOUT going
    // through the submit-success `close()` path below — the ceremony
    // RESOLVED, but nothing was shared. `succeeded: false` so a card-flip
    // listener can restore the completion actions without claiming a share
    // that didn't happen. Fire before `destroy()` — a forced retire drops the
    // callback, because a navigation teardown is not a resolution.
    this._notifyResolved(false);
    this.destroy();
  }

  /**
   * Fire the one-shot resolution callback. Nulled first, so a double close (an
   * auto-close whose `right-panel:closed` lands afterwards) can only restore once.
   * @private
   * @param {boolean} succeeded - True only from the submit-success `close()`
   *   path (via `_retire({force: false})`); false from every other resolution.
   */
  _notifyResolved(succeeded) {
    const onResolved = this._onResolved;
    this._onResolved = null;
    // `bothConfirmed` is meaningful only on the success path — nothing was
    // written on any other, so it is reported false rather than stale.
    if (onResolved) onResolved({ succeeded, bothConfirmed: succeeded && this._bothConfirmed });
  }

  /**
   * Close our own panel the ordinary way (the panel asking via
   * `requestClose`): a plain `right-panel:close`, so the panel slides out.
   *
   * @param {boolean} [succeeded=true] - Whether this close follows a real
   *   share (the approve-success auto-close) or is the overview's "Done"
   *   dismiss over an already-settled set. Decides what `onResolved` reports.
   */
  close(succeeded = true) {
    this._retire({ force: false, succeeded });
  }

  /**
   * Teardown: release the listener, the panel, the module reference — AND
   * actually close the panel.
   *
   * `SharingDetailPanel.destroy()` only resets the component's own mode; it
   * leaves the mounted DOM on screen. Without the close below, `switchMode()`
   * left a live queue sitting over the incoming session's chat.
   *
   * The close is FORCED (`RightPanelManager.close()` early-returns while a
   * handoff is pending, `forceClose` does not) because a teardown has to hold
   * regardless of panel-internal state, and it is emitted only while we still
   * own the content type — so it can never shut a foreign panel.
   */
  destroy() {
    this._retire({ force: true });
  }

  /**
   * @private
   * @param {{force: boolean, succeeded?: boolean}} options
   */
  _retire({ force, succeeded = false }) {
    if (this._destroyed) return;
    this._destroyed = true;
    // Unbind FIRST: `forceClose` emits `right-panel:closed` synchronously, and
    // that must not re-enter this teardown.
    eventBus.off('right-panel:closed', this._boundPanelClosed);

    // Release the queue run's input lock on EVERY retire path. LOAD-BEARING on
    // the forced branch: of the three chat exits, only `returnToDashboard`
    // emits its own `input:enabled` — `switchMode` and `clearModeDisplay` rely
    // entirely on this emit, so removing it as "redundant" would strand the
    // disabled overlay over the next conversation. (Known, inherited
    // limitation shared with QuestionnaireLauncher: `input:enabled` is
    // owner-less and clears whatever overlay is up.)
    if (this._inputLocked) {
      this._inputLocked = false;
      eventBus.emit('input:enabled');
    }

    const owned = this._contentType;
    this._contentType = null;
    this.panel?.handleClosed();
    this.panel?.destroy();
    this.panel = null;
    if (active === this) active = null;

    if (owned) {
      eventBus.emit('right-panel:close', force ? { force: true, reason: 'navigation' } : undefined);
    }

    // A FORCED retire is a navigation teardown (`destroySharingCeremony` from
    // `switchMode` / `returnToDashboard` / `clearModeDisplay`) — never a
    // resolution. Drop the callback so nothing can remount a completion group
    // into a chat the user has left. A non-forced retire is the panel closing
    // ITSELF via `requestClose`, which has two callers in SharingDetailPanel:
    // `_submitSucceeded`'s auto-close (passes the changed ids — a real share)
    // and the overview's "Done" (passes nothing — a dismiss). `succeeded`
    // carries that distinction in, so Done can never complete the card.
    if (force) {
      this._onResolved = null;
    } else {
      this._notifyResolved(succeeded);
    }
  }
}

/**
 * Open the in-chat sharing ceremony for one consent group.
 *
 * Reads `GET /couples/sharing-status`, maps it through the shared
 * `ledgerFromStatus`, and then either opens the queue or says why it can't:
 *   - fetch failed → the error notice;
 *   - no ledger / not-actionable state / empty scope → the "still preparing"
 *     notice (`openQueue`'s own guard is a silent no-op, which is exactly what
 *     an auto-open racing the register generation would hit);
 *   - already confirmed with nothing left to review → "You're all set".
 *
 * @param {Object} [params]
 * @param {string} [params.groupId='gestalt'] - Consent group to review; falls
 *   back to the ledger's gestalt group when the id isn't in the payload.
 * @param {Function|null} [params.onResolved] - Called once, as
 *   `onResolved({succeeded, bothConfirmed})`, when the ceremony resolves on its own
 *   (`succeeded: true` only for the approve-success auto-close; `false` for
 *   the user closing the panel, or a reason notice closing — never on a
 *   navigation teardown). Wired to `ModeOrchestrator.resolveSharingDecision`:
 *   card completed + phase 3 on `succeeded`, card `unfinished` + a re-offered
 *   decision otherwise. A reason notice carries it too — a ceremony that could
 *   not open must still hand the decision back, but never claims a share that
 *   never happened.
 * @returns {Promise<boolean>} Whether a queue actually opened.
 */
export async function launchSharingCeremony({ groupId = 'gestalt', onResolved = null } = {}) {
  if (launchInFlight) return false;
  launchInFlight = true;

  try {
    // A re-open supersedes whatever is still mounted (the in-flight guard only
    // covers concurrent clicks, not a second ceremony later in the session).
    // This bumps `launchToken`, so THIS launch's identity is taken after it.
    destroySharingCeremony();

    // Captured BEFORE the await — compared after it.
    const token = ++launchToken;
    const sessionAtLaunch = window.sessionManager?.getCurrentSessionId?.() ?? null;

    // A notice is owned like a queue is (see `openNotice`), so a teardown can
    // close either. Built lazily — a superseded launch must own nothing.
    const notice = (copy) => {
      active = new SharingCeremonyLauncher({ onResolved });
      active.openNotice(copy);
      return false;
    };

    const api = new DiscoveryAPI();
    let view = null;
    try {
      const res = await api.getSharingStatus();
      view = res?.data || null;
    } catch (err) {
      console.error('[sharingCeremonyLauncher] sharing-status read failed:', err);
      if (isSuperseded(token, sessionAtLaunch)) return false;
      return notice(ERROR_NOTICE);
    }

    if (isSuperseded(token, sessionAtLaunch)) return false;

    const partnerName = view?.partnerName || 'your partner';
    const ledger = ledgerFromStatus(view);
    const scopeId = ledger && ledger.groups.some(g => g.id === groupId)
      ? groupId
      : gestaltGroupId(ledger?.groups || []);

    if (!ledger || NOT_ACTIONABLE_STATES.has(view?.state) || !queueScope(ledger.units, scopeId).length) {
      return notice(PREPARING_NOTICE);
    }

    // Already settled by click time (review R4) — the same predicate
    // `SharingRegistry._reviewBannerHtml` suppresses its banner on. Reopening a
    // settled queue would ask for a confirm the user already gave.
    const group = ledger.groups.find(g => g.id === scopeId) || null;
    if (needsReviewCount(ledger.units) === 0 && group?.confirmed) {
      return notice(settledNotice(partnerName));
    }

    active = new SharingCeremonyLauncher({ ledger, partnerName, api, onResolved });
    active.open(scopeId);
    return true;
  } finally {
    launchInFlight = false;
  }
}

/**
 * Tear down the live ceremony, if any: close whatever ceremony content is on
 * the panel (queue OR reason notice), unbind, and invalidate any launch still
 * waiting on its status read. Idempotent, and safe to call from a synchronous
 * teardown — `ModeOrchestrator._teardownChatOwnedPanels()` does, from all three
 * chat exits (`switchMode`, `returnToDashboard`, `clearModeDisplay`).
 *
 * Staged consent is discarded by design: nothing has been written, and the
 * overview's "Approve & share" is the only batch confirm surface.
 *
 * @returns {boolean} Whether a mounted ceremony was torn down. False when there
 *   was nothing on the panel — an in-flight launch is still invalidated.
 */
export function destroySharingCeremony() {
  // Bumped UNCONDITIONALLY, before the mounted check: the case this exists for
  // is a teardown landing while a `getSharingStatus` read is still in flight,
  // when there is nothing mounted to find.
  launchToken += 1;
  if (!active) return false;
  active.destroy();
  active = null;
  return true;
}

/**
 * The live ceremony launcher, or null. Test seam — production code reaches the
 * ceremony only through {@link launchSharingCeremony} /
 * {@link destroySharingCeremony}.
 *
 * @returns {SharingCeremonyLauncher|null}
 */
export function getActiveSharingCeremony() {
  return active;
}
